import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { TextButton } from '@/shared/components/ui/button/text-button/text-button';
import { FullscreenLoader } from '@/shared/components/ui/fullscreen-loader/fullscreen-loader';
import { ErrorBoundary } from '@/shared/features/error/components/error-boundary/error-boundary';
import { NoPermission } from '@/shared/features/error/components/no-permission/no-permission';
import type { TPermissionAction } from '@/shared/features/permissions/actions';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useOwnUserRoles } from '@/shared/features/rbac/hooks/use-own-user-roles/use-own-user-roles';
import { useTypedMatches } from '@/shared/hooks/use-typed-matches/use-typed-matches';
import { TRoutePermission } from '@/shared/types/router.types';

const includesAction = (
  action: TPermissionAction | TPermissionAction[],
  expectedAction: TPermissionAction,
): boolean =>
  Array.isArray(action)
    ? action.includes(expectedAction)
    : action === expectedAction;

export const PermissionRoute = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const matches = useTypedMatches();
  const params = useParams();
  const {
    can,
    canAny,
    error: permissionsError,
    isLoading: isPermissionsLoading,
    retry: retryPermissions,
  } = usePermissionCheck();
  const {
    error: ownUserRolesError,
    isLoggedInUserRole,
    isLoading: isOwnUserRolesLoading,
    retry: retryOwnUserRoles,
  } = useOwnUserRoles();

  const permissions = matches
    .map((match) => match.handle?.permission)
    .filter((permission): permission is TRoutePermission =>
      Boolean(permission),
    );
  const needsOwnUserRoles = permissions.some((permission) =>
    includesAction(permission.action, 'ident:readUserRole'),
  );

  if (isPermissionsLoading || (needsOwnUserRoles && isOwnUserRolesLoading)) {
    return <FullscreenLoader />;
  }

  if (permissionsError || (needsOwnUserRoles && ownUserRolesError)) {
    const retry = () => {
      retryPermissions?.();
      if (needsOwnUserRoles) {
        retryOwnUserRoles();
      }
    };

    return (
      <ErrorBoundary
        message={<TextButton onClick={retry}>{t('common:retry')}</TextButton>}
      />
    );
  }

  const allowed = permissions.every((permission) => {
    const id = permission.idParam ? params[permission.idParam] : undefined;

    // A user may always read a role they belong to, even without the policy.
    const isOwnRoleRead =
      includesAction(permission.action, 'ident:readUserRole') &&
      isLoggedInUserRole(params.userRoleId);

    if (isOwnRoleRead) {
      if (!Array.isArray(permission.action) || permission.any) {
        return true;
      }

      const remainingActions = permission.action.filter(
        (action) => action !== 'ident:readUserRole',
      );

      return remainingActions.length === 0 || can(remainingActions, id);
    }

    return permission.any
      ? canAny(permission.action, id)
      : can(permission.action, id);
  });

  if (!allowed) {
    return <NoPermission />;
  }

  return <>{children}</>;
};
